<template>
  <div class="summary">
    <div class="summary__inner">
      <button
        type="button"
        class="close"
        @click.prevent="deployerStore.summaryMobileOpened = false"
        v-html="XSvg"
      />
      <div class="grid-row">
        <div class="title">
          {{ texts.title }}
        </div>
        <div class="group">
          <div class="subtitle">
            {{ texts.price_feeds }}
            <button
              v-show="props.oracleValues.selectedAssets.length > 0"
              type="button"
              class="clear"
              @click.prevent="emits('clear-all')"
            >
              Clear all
            </button>
          </div>
          <div class="feeds">
            <DeployerUiFeedChip
              v-for="item in props.oracleValues.selectedAssets"
              :feed="item.symbol"
              @remove="removeFeed(item)"
            />
          </div>
        </div>

        <div class="group">
          <div class="subtitle">
            {{ texts.update_triggers }}
          </div>
          <DeployerDeploySummaryRow :values="triggers" />
        </div>

        <div class="group">
          <div class="subtitle">
            {{ texts.network }}
          </div>
          <DeployerDeploySummaryRow :values="network" />
        </div>

        <div class="group">
          <div class="subtitle">
            {{ texts.additional_settings }}
          </div>
          <DeployerDeploySummaryRow
            :values="additionalSettings"
            :svg-fill="true"
          />
        </div>
        <hr />

        <div class="group">
          <div class="subtitle">
            {{ texts.costs_and_pricing }}
          </div>

          <div class="cost-row">
            <div class="cost-label">
              {{ texts.deployment_costs }}
            </div>
            <div class="cost-value">
              <strong>0.05 ETH</strong>
            </div>
          </div>
          <div class="cost-row">
            <div class="cost-label">
              {{ texts.time_to_deploy }}
            </div>
            <div class="cost-value"><strong>3 minutes</strong> ≈ 4 txs</div>
          </div>
        </div>
      </div>
    </div>
    <template v-if="newWalletStore?.account?.address">
      <NuxtLink
        v-if="!accountStore?.account?.plan?.plan_id"
        class="btn cta w100"
        to="/account/plan/"
      >
        Select plan to deploy
      </NuxtLink>

      <button
        v-else-if="isPaymentProcessed"
        type="button"
        class="btn cta w100"
        :disabled="!props.canDeploy"
        @click.prevent="emits('deploy')"
      >
        {{ texts.deploy_oracle }}
      </button>

      <button
        v-else
        type="button"
        class="btn cta w100"
        :disabled="!props.canDeploy"
        @click.prevent="emits('pay')"
      >
        {{ texts.pay_and_deploy_oracle }}
      </button>
    </template>

    <button
      v-else
      type="button"
      class="btn cta w100"
      @click.prevent="emits('login')"
    >
      {{ texts.login }}
    </button>
  </div>
</template>

<script setup>
import HeartbeatSvg from '~/layers/base/assets/icons/heartbeat.svg?raw'
import DeviationSvg from '~/layers/base/assets/icons/deviation.svg?raw'
import NetworkSvg from '~/layers/base/assets/icons/network.svg?raw'
import RpcSvg from '~/layers/base/assets/icons/rpc.svg?raw'
import LinkAdapterSvg from '~/layers/base/assets/icons/link-adapter.svg?raw'
import GasSvg from '~/layers/base/assets/icons/gas.svg?raw'
import XSvg from '~/layers/base/assets/icons/x.svg?raw'

const props = defineProps({
  oracleValues: Object,
  canDeploy: Boolean,
  isPaymentProcessed: Boolean
})

const deployerStore = useDeployerStore()
const accountStore = useAccountStore()
const textsStore = useTextsStore()
const texts = computed(() => {
  return textsStore.deploy.sidebar
})

const newWalletStore = useNewWalletStore()

const emits = defineEmits([
  'deploy',
  'login',
  'pay',
  'remove-feed',
  'clear-all'
])

const mobileExpanded = ref(false)

const removeFeed = (feed) => {
  emits('remove-feed', feed)
}

const triggers = computed(() => {
  return [
    {
      icon: HeartbeatSvg,
      value: props.oracleValues.heartbeat + props.oracleValues.heartbeatUnit,
      label: ' heartbeat'
    },
    {
      icon: DeviationSvg,
      value: props.oracleValues.deviation,
      label: '% deviation'
    }
  ]
})

const network = computed(() => {
  return [
    {
      icon: NetworkSvg,
      value: props.oracleValues.chainId,
      prefix: 'ID '
    },
    {
      icon: RpcSvg,
      value: props.oracleValues.rpc_url
    }
  ]
})

const additionalSettings = computed(() => {
  return [
    {
      icon: LinkAdapterSvg,
      value: props.oracleValues.chainlinkAdapter ? ' ' : '',
      prefix: 'LINK adapter'
    },
    {
      icon: GasSvg,
      value: props.oracleValues.prefillOracle
        ? props.oracleValues.prefillValue +
          ' ' +
          props.oracleValues.prefillCurrency
        : ''
    }
  ]
})
</script>

<style lang="scss" scoped>
.summary {
  @media (max-width: 1023px) {
  }

  &__inner {
    @media (min-width: 1024px) {
      max-height: calc(90dvh - 80px);
      overflow-y: auto;
    }

    & {
      padding: 20px 20px 0 20px;
    }
    // @media (max-width: 1023px) {
    //   display: grid;
    //   grid-template-rows: 0fr;
    //   transition: grid-template-rows 0.3s, padding 0.3s;
    //   padding: 0px 20px;
    //   .grid-row {
    //     overflow: hidden;
    //   }
    // }

    // .mobileExpanded & {
    //   @media (max-width: 1023px) {
    //     grid-template-rows: 1fr;

    //     padding: 20px 20px 0 20px;
    //   }
    // }
  }

  .btn {
    margin: 20px;
    width: calc(100% - 40px);
  }

  hr {
    border: 0;
    border-top: 1px solid var(--bubble-10, rgba(0, 0, 0, 0.1));
    margin: 15px -20px 20px;

    @media (max-width: 1023px) {
      margin-inline: 0;
    }
  }
}

.group {
  margin-bottom: 25px;

  &:last-child {
    margin-bottom: 0;
  }
}

.title {
  font-size: 2.2rem;
  margin-bottom: 20px;
}

.subtitle {
  font-size: 1.5rem;
  font-weight: 500;
  margin-bottom: 10px;
}

.button {
  &[disabled] {
    opacity: 0.5;
  }
}

.close {
  position: absolute;
  right: 20px;
  top: 17px;

  :deep(svg) {
    width: 24px;
    height: 24px;
  }
}

.feeds {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.cost-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 1.3rem;
}

.cost-label {
  opacity: 0.7;
}

.clear {
  color: var(--CTA, #3d6ef6);
  font-size: 1.5rem;
  margin-left: 5px;
  opacity: 0.8;
  &:hover {
    text-decoration: underline;
  }
}
</style>
